/**
 * QuestionRenderer Component
 * 
 * Renders a single question based on its type
 * Supports all question types from Question Bank including new 'radio' type
 */

import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Info } from 'lucide-react';
import { GuidanceButton } from './GuidanceButton';
import type { Question } from '../types/question-bank';

interface QuestionRendererProps {
  question: Question;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  disabled?: boolean;
}

export function QuestionRenderer({
  question,
  value,
  onChange,
  error,
  disabled = false
}: QuestionRendererProps) {
  
  const renderInput = () => {
    switch (question.type) {
      case 'text':
        return (
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder}
            disabled={disabled}
            rows={4}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            placeholder={question.placeholder}
            disabled={disabled}
            min={question.validation?.min}
            max={question.validation?.max}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={value === true || value === 'true'}
              onCheckedChange={(checked) => onChange(checked)}
              disabled={disabled}
            />
            <Label className="text-sm text-muted-foreground">
              {value === true || value === 'true' ? 'Yes' : 'No'}
            </Label>
          </div>
        );

      case 'radio':
        return (
          <RadioGroup 
            value={value || ''} 
            onValueChange={onChange} 
            disabled={disabled} 
            className="space-y-3 pt-2"
          >
            {question.options?.map((option) => (
              <div key={option.value} className="flex items-start space-x-2">
                <RadioGroupItem value={option.value} id={`${question.id}-${option.value}`} className="mt-1" />
                <Label htmlFor={`${question.id}-${option.value}`} className="font-normal cursor-pointer leading-relaxed">
                  {option.label}
                  {option.description && (
                    <div className="text-xs text-muted-foreground mt-0.5">{option.description}</div>
                  )}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'select':
        return (
          <Select
            value={value || ''}
            onValueChange={onChange}
            disabled={disabled}
          >
            <SelectTrigger className={error ? 'border-red-500' : ''}>
              <SelectValue placeholder={question.placeholder || 'Select an option'} />
            </SelectTrigger>
            <SelectContent>
              {question.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div>
                    <div>{option.label}</div>
                    {option.description && (
                      <div className="text-xs text-muted-foreground">{option.description}</div>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'multiselect': {
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {question.options?.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`${question.id}-${option.value}`}
                  checked={selectedValues.includes(option.value)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onChange([...selectedValues, option.value]);
                    } else {
                      onChange(selectedValues.filter((v: string) => v !== option.value));
                    }
                  }}
                  disabled={disabled}
                />
                <Label
                  htmlFor={`${question.id}-${option.value}`}
                  className="text-sm cursor-pointer"
                >
                  <div>{option.label}</div>
                  {option.description && (
                    <div className="text-xs text-muted-foreground">{option.description}</div>
                  )}
                </Label>
              </div>
            ))}
          </div>
        );
      }

      default:
        return (
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
          />
        );
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={question.id} className="text-base font-medium">
          {question.text}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </Label>

        {/* Replaced manual dialog with GuidanceButton */}
        <GuidanceButton question={question} />
      </div>
      
      {question.description && (
        <p className="text-sm text-muted-foreground">{question.description}</p>
      )}

      {renderInput()}

      {question.helpText && !error && (
        <p className="text-xs text-muted-foreground">{question.helpText}</p>
      )}

      {question.examples && question.examples.length > 0 && !error && (
        <p className="text-xs text-muted-foreground italic">
          Example: {question.examples[0]}
        </p>
      )}

      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}

      {question.validation?.errorMessage && error && (
        <p className="text-xs text-red-500">{question.validation.errorMessage}</p>
      )}

      {/* Conditional Content (Inline Examples / Warnings) */}
      {question.conditionalContent?.map((content, idx) => {
        if (content.value === value) {
          return (
            <div key={idx} className={`mt-2 p-3 rounded-md text-sm animate-in fade-in slide-in-from-top-1 ${
              content.type === 'warning' ? 'bg-orange-50 text-orange-800 border border-orange-200' :
              content.type === 'recommendation' ? 'bg-blue-50 text-blue-800 border border-blue-200' :
              'bg-slate-100 text-slate-800 border border-slate-200'
            }`}>
              {content.text}
            </div>
          );
        }
        return null;
      })}
      
      {/* Confidence Impact / Recommendation Display */}
      {question.confidenceImpact?.map((impact, idx) => {
        if (impact.value === value && impact.recommendation) {
          return (
            <div key={`impact-${idx}`} className={`mt-2 p-3 rounded-md text-sm border flex items-start gap-2 animate-in fade-in slide-in-from-top-1 ${
              impact.confidence === 'low' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-blue-50 border-blue-200 text-blue-900'
            }`}>
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold block mb-1">Recommendation:</span>
                {impact.recommendation}
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}
